import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons';
import CustomerCareDTable from '../../Shared/CustomerCareDTable';
import {
  customError,
  customSuccess,
  dimGrey,
  lightBlack,
  outlineSeparator,
} from '../../../mainTheme';
import { parseDateToLocal } from '../../../shared/utils';
import { PharmacyUpload } from '../../../store/pharmacyPatients/pharmacyPatientsTypes';

interface MedicationUploadTabProps {
  uploads: PharmacyUpload[];
  onUploadClick: () => void;
  loading?: boolean;
}

// The status chip reduces processing_result to a binary outcome: a stored
// upload ("Uploaded", or legacy "Success"/"SUCCESS" spellings) shows as
// Success, anything else as Failure. Intermediate backend states are
// deliberately not surfaced in this view. Null/empty (rows predating the
// column) stays a neutral placeholder.
const SUCCESS_STATUSES = ['success', 'uploaded'];

const getStatusDisplay = (
  result: string | null | undefined,
): { label: string; color: string } => {
  if (!result) {
    return { label: '—', color: dimGrey };
  }
  if (SUCCESS_STATUSES.includes(result.toLowerCase())) {
    return { label: 'Success', color: customSuccess };
  }
  return { label: 'Failure', color: customError };
};

const formatDate = (value: string | null | undefined): string =>
  value ? parseDateToLocal(value, 'MMM dd, yyyy h:mm a') : 'N/A';

const MedicationUploadTab: React.FC<MedicationUploadTabProps> = ({
  uploads,
  onUploadClick,
  loading = false,
}) => {
  const columns = [
    { field: 'file_name', headerName: 'File name', flex: 1.5 },
    {
      field: 'uploaded_by',
      headerName: 'Uploaded by',
      flex: 1,
      renderCell: (params: any) => params.value || 'N/A',
    },
    {
      field: 'uploaded_at',
      headerName: 'Uploaded at',
      flex: 1,
      renderCell: (params: any) => formatDate(params.value),
    },
    {
      field: 'processing_completed_at',
      headerName: 'Completed at',
      flex: 1,
      renderCell: (params: any) => formatDate(params.value),
    },
    {
      field: 'processing_result',
      headerName: 'Status',
      flex: 0.6,
      renderCell: (params: any) => {
        const { label, color } = getStatusDisplay(params.value);
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: `${color}22`,
              color,
              fontWeight: 700,
              fontSize: '12px',
            }}
          />
        );
      },
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '0.2px',
            color: lightBlack,
          }}
        >
          Medication uploads
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
          onClick={onUploadClick}
          sx={{ height: '40px' }}
        >
          Upload new medication
        </Button>
      </Box>

      {!loading && uploads.length === 0 ? (
        <Box
          p="32px"
          borderRadius="8px"
          border={`1px dashed ${outlineSeparator}`}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              color: lightBlack,
            }}
          >
            No medication uploads yet
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: dimGrey,
            }}
          >
            Upload a CSV, PGP, or GPG file to get started.
          </Typography>
        </Box>
      ) : (
        <CustomerCareDTable
          rows={uploads}
          columns={columns}
          loading={loading}
          pageSize={10}
          defaultSortField="uploaded_at"
          defaultSortDirection="desc"
          hideViewAllButton
          minHeight="200px"
          idField="upload_id"
        />
      )}
    </Box>
  );
};

export default MedicationUploadTab;
